import React from 'react';
import { 
  Globe, 
  Users, 
  Coffee,
  Heart,
  Brain,
  Laptop,
  GraduationCap,
  Mail
} from 'lucide-react';
import HeroImage from '../components/HeroImage';
import AnimatedSection from '../components/AnimatedSection';

function Careers() {
  const GOOGLE_FORM_URL = "https://docs.google.com/forms/d/e/1FAIpQLSfKLHkPyKGOxjUMO3-sN5oQwwlnmhONJjOHqXEwUcaamfPeJQ/viewform?usp=header";

  const benefits = [
    {
      icon: <Globe className="h-8 w-8 text-[#00ffff]" />,
      title: "Remote-First Culture",
      description: "Work from anywhere in the world with our distributed team. We believe in hiring the best talent regardless of location, fostering a diverse and inclusive workplace that brings together perspectives from different cultures and backgrounds."
    },
    {
      icon: <Brain className="h-8 w-8 text-[#00ffff]" />,
      title: "Continuous Learning",
      description: "Access to unlimited courses, conferences, and learning resources. We provide dedicated learning time and budget for professional development, ensuring you stay at the forefront of AI technology and industry trends."
    },
    {
      icon: <Heart className="h-8 w-8 text-[#00ffff]" />,
      title: "Health & Wellness",
      description: "Comprehensive health coverage and wellness programs. We offer premium health insurance, mental health support, fitness allowances, and wellness initiatives to ensure our team maintains a healthy work-life balance."
    },
    {
      icon: <Coffee className="h-8 w-8 text-[#00ffff]" />,
      title: "Work-Life Balance",
      description: "Flexible hours and unlimited PTO policy. We trust our team to manage their time effectively and believe in measuring results, not hours worked. Take the time you need to recharge and stay productive."
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen mx-[4.2mm] mt-[4.2mm]">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#00ffff]/10 via-transparent to-[#ff0000]/10 animate-gradient rounded-lg z-10"></div>
          <div className="absolute inset-0 rounded-lg overflow-hidden">
            <HeroImage 
              src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&q=80&w=1920&h=1080" 
              alt="Team collaboration"
              priority={true}
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50 rounded-lg z-20"></div>
        </div>

        <div className="relative h-full flex items-center z-30">
          <div className="max-w-7xl mx-auto px-4">
            <h1 className="text-5xl font-bold text-center text-white mb-6">Join Team Atlas</h1>
            <p className="text-xl text-white/90 text-center max-w-3xl mx-auto">
              At Atlas AI Business Solutions, we're a team of problem solvers dedicated to achieving the impossible.
            </p>
          </div>
        </div>
      </section>

      {/* Team Philosophy Section */}
      <div className="max-w-7xl mx-auto px-4 py-20">
        <AnimatedSection>
          <div className="white-holographic-card p-12 rounded-2xl">
            <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">We Want Problem Solvers</h2>
            <div className="space-y-8 text-center">
              <p className="text-xl text-gray-800">
                Competition drives excellence. Excellence is what our customers demand, and in turn, so do we. We are an exceptional team of problem solvers who find solutions where there aren't any by thinking outside the box and expecting each other to always perform. Every member of Team Atlas carries the weight of success on their shoulders, together.
              </p>
              <p className="text-xl text-gray-800">
                Think you've got what it takes? Shoot us your resume. We'll be in touch.
              </p>
              <div className="flex items-center justify-center text-gray-800">
                <Mail className="h-5 w-5 mr-2 text-[#00ffff]" />
                <a href="mailto:jz@think-atlasai.com" className="text-[#00ffff] hover:text-[#ff0000] transition-colors">
                  jz@think-atlasai.com
                </a>
              </div>
            </div>
          </div>
        </AnimatedSection>
      </div>

      {/* Why Join Us Section */}
      <div className="max-w-7xl mx-auto px-4 py-20">
        <h2 className="text-3xl font-bold text-white mb-12">Why Join Atlas AI?</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {benefits.map((benefit, index) => (
            <AnimatedSection key={index}>
              <div className="white-holographic-card p-8 rounded-xl">
                <div className="mb-4">{benefit.icon}</div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </div>

      {/* Our Values Section */}
      <div className="max-w-7xl mx-auto px-4 py-20">
        <AnimatedSection>
          <div className="white-holographic-card p-12 rounded-2xl">
            <h2 className="text-3xl font-bold text-gray-800 mb-12 text-center">Our Values</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <Users className="h-12 w-12 text-[#00ffff]" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Collaboration</h3>
                <p className="text-gray-600">
                  We believe great ideas come from working together and sharing diverse perspectives. Our team thrives on open communication, mutual respect, and the collective pursuit of excellence. Every voice matters, and every team member contributes to our shared success.
                </p>
              </div>
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <Laptop className="h-12 w-12 text-[#00ffff]" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Innovation</h3>
                <p className="text-gray-600">
                  We push boundaries and embrace new technologies to solve complex challenges. Our team is encouraged to experiment, take calculated risks, and think outside the box. We're not just following trends – we're setting them.
                </p>
              </div>
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <GraduationCap className="h-12 w-12 text-[#00ffff]" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Growth</h3>
                <p className="text-gray-600">
                  We invest in our team's development and celebrate continuous learning. Every day presents new opportunities to grow, both professionally and personally. We provide the resources and support needed to help you reach your full potential.
                </p>
              </div>
            </div>
          </div>
        </AnimatedSection>
      </div>
    </div>
  );
}

export default Careers;